import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Save, Plus, Trash2, Building2, Package, Check, X, Clock, ArrowRight, CreditCard, CheckCircle2, ExternalLink, Ban, RotateCcw, AlertTriangle } from "lucide-react";
import IcalSubscribe from "@/components/IcalSubscribe";
import DataResetDialog from "@/components/DataResetDialog";
import { useAuth } from "@/contexts/AuthContext";

function fmtCountdown(iso) {
  if (!iso) return "";
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "abgelaufen";
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  if (d > 0) return `Aktion endet in ${d} Tag${d === 1 ? "" : "en"}`;
  const m = Math.floor((ms % 3600000) / 60000);
  return `Aktion endet in ${h} Std ${m} Min`;
}

const FEATURE_LABELS_SHORT = {
  cms_pages: "Seitenbuilder", theme_studio: "Theme Studio", header_footer: "Header & Footer",
  blog: "Blog", gallery: "Galerie", forms: "Formulare", seo: "SEO",
  services: "Leistungen", service_categories: "Service-Kategorien", addons: "Zusatzleistungen",
  staff: "Mitarbeiter", staff_blocks: "Urlaub & Krankheit", availability: "Verfügbarkeit",
  bookings: "Buchungen / Kalender", ical_sync: "iCal-Sync", newsletter: "Newsletter",
  email_templates: "E-Mail-Vorlagen", email_smtp: "E-Mail SMTP",
  loyalty: "Treueprogramm", analytics: "Auswertung", customer_portal: "Kundenbereich",
};

export default function SettingsPage() {
  const { refresh, plan: currentPlan, user, salon } = useAuth();
  const [resetOpen, setResetOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [profile, setProfile] = useState(null);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);
  const [users, setUsers] = useState([]);
  const [tokens, setTokens] = useState({});
  const [newUser, setNewUser] = useState({ email: "", name: "", password: "", role: "salon_admin" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [plans, setPlans] = useState([]);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [planSwitchBusy, setPlanSwitchBusy] = useState(false);
  const [confirmPlanId, setConfirmPlanId] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [paymentBusy, setPaymentBusy] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);  // {status, payment_status, credited}
  const [params, setParams] = useSearchParams();

  const load = async () => {
    const [s, u, t, p, pl, sub] = await Promise.all([
      api.get("/cms/settings"),
      api.get("/cms/users"),
      api.get("/cms/ical-token"),
      api.get("/cms/salon"),
      api.get("/public/plans"),
      api.get("/cms/billing/subscription"),
    ]);
    setSettings(s.data);
    setUsers(u.data);
    setTokens(t.data || {});
    setProfile({ name: p.data.name || "", slug: p.data.slug || "", subdomain: p.data.subdomain || "" });
    setPlans(pl.data || []);
    setSubscription(sub.data || null);
  };
  useEffect(() => { load(); }, []);

  // Poll Stripe session result if redirected back with ?stripe_session_id=...
  useEffect(() => {
    const sid = params.get("stripe_session_id");
    if (!sid || paymentBusy) return;
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/cms/billing/sync/${sid}`);
        if (cancelled) return;
        setPaymentStatus({ payment_status: data.payment_status, status: data.status, credited: !!data.subscription_id });
        if (data.payment_status === "paid" || data.payment_status === "no_payment_required" || data.status === "complete" || data.status === "expired" || attempts > 5) {
          if (refresh) await refresh();
          load();
          params.delete("stripe_session_id");
          setParams(params, { replace: true });
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (attempts < 5) setTimeout(poll, 2500);
      }
    };
    setPaymentBusy(true);
    poll().finally(() => setPaymentBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);
  if (!settings || !profile) return <div className="p-8">Lade...</div>;

  const saveProfile = async () => {
    setProfileSaving(true);
    setProfileMsg(null);
    try {
      const { data } = await api.put("/cms/salon", {
        name: profile.name.trim(),
        slug: profile.slug.trim().toLowerCase(),
      });
      setProfile({ name: data.name, slug: data.slug, subdomain: data.subdomain });
      setProfileMsg({ ok: true, text: "Firmendaten gespeichert." });
      if (refresh) await refresh();
    } catch (e2) {
      setProfileMsg({ ok: false, text: e2.response?.data?.detail || "Speichern fehlgeschlagen" });
    } finally {
      setProfileSaving(false);
    }
  };

  const switchPlan = async (planId) => {
    setPlanSwitchBusy(true);
    try {
      const plan = plans.find((p) => p.id === planId);
      const price = plan?.on_sale ? plan.sale_price : plan?.monthly_price || 0;
      const hasActiveSub = subscription?.has_subscription && subscription?.status !== "canceled";

      if (price <= 0) {
        // Free plan
        const { data } = await api.post("/cms/billing/change-plan", { plan_id: planId });
        if (data?.ok) {
          if (refresh) await refresh();
          setPlanModalOpen(false); setConfirmPlanId(null); load();
        }
      } else if (hasActiveSub) {
        // Pro-rata plan change on existing subscription
        const { data } = await api.post("/cms/billing/change-plan", { plan_id: planId });
        if (data?.needs_checkout) {
          // Should not happen since hasActiveSub, but fall back
          const res = await api.post("/cms/billing/subscribe", { plan_id: planId, origin_url: window.location.origin });
          if (res.data?.url) { window.location.href = res.data.url; return; }
        }
        if (refresh) await refresh();
        setPlanModalOpen(false); setConfirmPlanId(null); load();
      } else {
        // First subscription → Stripe Checkout
        const { data } = await api.post("/cms/billing/subscribe", { plan_id: planId, origin_url: window.location.origin });
        if (data?.url) { window.location.href = data.url; return; }
      }
    } catch (e2) {
      alert(e2.response?.data?.detail || "Paketwechsel fehlgeschlagen");
    } finally {
      setPlanSwitchBusy(false);
    }
  };

  const payCurrentPlan = async () => {
    if (!currentPlan) return;
    setPaymentBusy(true);
    try {
      const { data } = await api.post("/cms/billing/subscribe", {
        plan_id: currentPlan.id,
        origin_url: window.location.origin,
      });
      if (data?.url) { window.location.href = data.url; return; }
      if (data?.free) { if (refresh) await refresh(); load(); }
    } catch (e2) {
      alert(e2.response?.data?.detail || "Abo konnte nicht gestartet werden");
    } finally {
      setPaymentBusy(false);
    }
  };

  const openPortal = async () => {
    try {
      const { data } = await api.post("/cms/billing/portal", { origin_url: window.location.origin });
      if (data?.url) window.location.href = data.url;
    } catch (e2) {
      alert(e2.response?.data?.detail || "Portal konnte nicht geöffnet werden");
    }
  };

  const cancelSub = async () => {
    if (!window.confirm("Abo wirklich zum Ende des Abrechnungszeitraums kündigen?")) return;
    try {
      await api.post("/cms/billing/cancel");
      load();
    } catch (e2) {
      alert(e2.response?.data?.detail || "Kündigung fehlgeschlagen");
    }
  };

  const resumeSub = async () => {
    try {
      await api.post("/cms/billing/resume");
      load();
    } catch (e2) {
      alert(e2.response?.data?.detail || "Reaktivierung fehlgeschlagen");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/cms/settings", {
        min_lead_hours: parseInt(settings.min_lead_hours) || 0,
        max_lead_days: parseInt(settings.max_lead_days) || 60,
        staff_see_all_bookings: !!settings.staff_see_all_bookings,
        staff_selection_required: !!settings.staff_selection_required,
        slot_step_minutes: parseInt(settings.slot_step_minutes) || 15,
        timezone: settings.timezone || "Europe/Berlin",
        reminder_hours_before: parseInt(settings.reminder_hours_before ?? 24),
      });
    } finally { setSaving(false); }
  };

  const promote = async (e) => {
    e.preventDefault(); setErr("");
    try {
      await api.post("/cms/users/promote", newUser);
      setNewUser({ email: "", name: "", password: "", role: "salon_admin" });
      load();
    } catch (e2) { setErr(e2.response?.data?.detail || "Fehler"); }
  };

  const removeUser = async (uid) => {
    if (!window.confirm("Nutzer entfernen?")) return;
    await api.delete(`/cms/users/${uid}`);
    load();
  };

  const ROLE_LABEL = { salon_admin: "Salon-Admin", staff_manager: "Manager", staff_member: "Mitarbeiter" };

  return (
    <div className="p-8 lg:p-12 mv-fade-in max-w-5xl">
      <div className="flex items-end justify-between mb-10">
        <div><p className="mv-label mb-2">Konfiguration</p><h1 className="font-display text-4xl">Einstellungen</h1></div>
        <button data-testid="save-settings-btn" onClick={save} disabled={saving} className="mv-btn"><Save className="w-4 h-4" /> {saving ? "..." : "Speichern"}</button>
      </div>

      <div className="mv-card p-6 mb-6" data-testid="company-profile-card">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--mv-text-muted)" }} />
            <p className="mv-label">Firmendaten</p>
          </div>
          <button
            data-testid="save-profile-btn"
            onClick={saveProfile}
            disabled={profileSaving || !profile.name.trim() || !profile.slug.trim()}
            className="mv-btn"
          >
            <Save className="w-4 h-4" /> {profileSaving ? "..." : "Speichern"}
          </button>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs mb-1">Firmenname</label>
            <input
              data-testid="company-name-input"
              className="mv-input"
              value={profile.name}
              onChange={(e) => setProfile({ ...profile, name: e.target.value })}
              placeholder="z. B. Bella Capelli"
            />
            <p className="text-xs mt-1" style={{ color: "var(--mv-text-muted)" }}>Wird in E-Mails, Buchungen und auf der öffentlichen Website angezeigt.</p>
          </div>
          <div>
            <label className="block text-xs mb-1">URL-Kürzel (Slug)</label>
            <div className="flex items-center gap-2">
              <span className="text-xs" style={{ color: "var(--mv-text-muted)" }}>/salon/</span>
              <input
                data-testid="company-slug-input"
                className="mv-input flex-1 font-mono"
                value={profile.slug}
                onChange={(e) => setProfile({ ...profile, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
                placeholder="bellacapelli"
              />
            </div>
            <p className="text-xs mt-1" style={{ color: "var(--mv-text-muted)" }}>
              {profile.subdomain ? `Subdomain: ${profile.subdomain}` : "—"} · Achtung: bestehende Links auf alte URL funktionieren nach Änderung nicht mehr.
            </p>
          </div>
        </div>
        {profileMsg && (
          <p
            data-testid="profile-msg"
            className="mt-3 text-xs px-3 py-2 rounded"
            style={{
              background: profileMsg.ok ? "rgba(34,197,94,0.1)" : "rgba(220,38,38,0.1)",
              color: profileMsg.ok ? "#15803d" : "#b91c1c",
            }}
          >
            {profileMsg.text}
          </p>
        )}
      </div>

      {/* Payment success/failed banner from Stripe redirect */}
      {paymentStatus && (
        <div
          data-testid="payment-result-banner"
          className="mb-6 p-4 rounded-lg flex items-start gap-3"
          style={{
            background: paymentStatus.payment_status === "paid" ? "rgba(34,197,94,0.1)" : "rgba(176,124,47,0.1)",
            color: paymentStatus.payment_status === "paid" ? "#15803d" : "#92400e",
          }}
        >
          {paymentStatus.payment_status === "paid" ? (
            <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
          ) : (
            <Clock className="w-5 h-5 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
          )}
          <div>
            <p className="font-medium text-sm">
              {paymentStatus.payment_status === "paid"
                ? "Zahlung erfolgreich — Paket ist für 30 Tage aktiv."
                : `Zahlung läuft… (Status: ${paymentStatus.payment_status})`}
            </p>
          </div>
          <button onClick={() => setPaymentStatus(null)} className="ml-auto p-1 -m-1 opacity-60 hover:opacity-100">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {params.get("stripe_canceled") && (
        <div className="mb-6 p-4 rounded-lg" style={{ background: "rgba(176,124,47,0.1)", color: "#92400e" }}>
          <p className="text-sm">Zahlungsvorgang abgebrochen. Du kannst es jederzeit erneut versuchen.</p>
        </div>
      )}

      {/* My Plan card */}
      <div className="mv-card p-6 mb-6" data-testid="my-plan-card">
        <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--mv-text-muted)" }} />
            <p className="mv-label">Mein Paket</p>
          </div>
          <button
            data-testid="change-plan-btn"
            onClick={() => setPlanModalOpen(true)}
            className="mv-btn-ghost text-sm"
          >
            Paket wechseln <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
        {currentPlan ? (
          <div>
            <div className="flex items-baseline gap-3 flex-wrap mb-1">
              <h3 className="font-display text-2xl">{currentPlan.name}</h3>
              {currentPlan.on_sale ? (
                <p>
                  <span className="font-medium" style={{ color: "var(--mv-primary)" }}>
                    {currentPlan.sale_price.toFixed(2)} €
                  </span>
                  <span className="line-through ml-1.5 text-sm" style={{ color: "var(--mv-text-muted)" }}>
                    {currentPlan.monthly_price.toFixed(2)} €
                  </span>
                  <span className="text-xs" style={{ color: "var(--mv-text-muted)" }}> / Monat</span>
                </p>
              ) : (
                <p>
                  <span className="font-medium">{currentPlan.monthly_price.toFixed(2)} €</span>
                  <span className="text-xs" style={{ color: "var(--mv-text-muted)" }}> / Monat</span>
                </p>
              )}
            </div>
            {currentPlan.description && (
              <p className="text-sm mb-3" style={{ color: "var(--mv-text-muted)" }}>{currentPlan.description}</p>
            )}
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-xs" style={{ color: "var(--mv-text-muted)" }}>
                {(currentPlan.features || []).length} Module aktiviert
              </p>
              {subscription && (
                <p className="text-xs" data-testid="subscription-status">
                  {subscription.cancel_at_period_end && (subscription.active_until || subscription.trial_end) ? (
                    <span style={{ color: "#92400e" }}>
                      ⚠ Wird zum {new Date(subscription.active_until || subscription.trial_end).toLocaleDateString("de-DE")} gekündigt
                    </span>
                  ) : subscription.status === "trialing" && subscription.trial_end ? (
                    <span style={{ color: "#92400e" }}>
                      🎁 Testphase bis {new Date(subscription.trial_end).toLocaleDateString("de-DE")}
                    </span>
                  ) : subscription.status === "active" && subscription.active_until ? (
                    <span style={{ color: "#15803d" }}>
                      ✓ Aktiv · verlängert sich am {new Date(subscription.active_until).toLocaleDateString("de-DE")}
                    </span>
                  ) : subscription.status === "past_due" ? (
                    <span style={{ color: "#b91c1c" }}>
                      ⚠ Zahlung ausstehend
                    </span>
                  ) : currentPlan.effective_price > 0 ? (
                    <span style={{ color: "#b91c1c" }}>
                      ⚠ Kein aktives Abo
                    </span>
                  ) : (
                    <span style={{ color: "var(--mv-text-muted)" }}>Kostenloses Paket</span>
                  )}
                </p>
              )}
            </div>
            {currentPlan.effective_price > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {!subscription?.has_subscription || subscription?.status === "canceled" ? (
                  <button
                    data-testid="pay-now-btn"
                    onClick={payCurrentPlan}
                    disabled={paymentBusy}
                    className="mv-btn"
                  >
                    <CreditCard className="w-4 h-4" strokeWidth={1.5} />
                    {paymentBusy ? "Lade…" : "Abo abschließen (14 Tage gratis)"}
                  </button>
                ) : (
                  <>
                    <button
                      data-testid="open-portal-btn"
                      onClick={openPortal}
                      className="mv-btn-ghost"
                    >
                      <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
                      Karte / Rechnungen verwalten
                    </button>
                    {subscription?.cancel_at_period_end ? (
                      <button
                        data-testid="resume-sub-btn"
                        onClick={resumeSub}
                        className="mv-btn-ghost"
                      >
                        <RotateCcw className="w-4 h-4" strokeWidth={1.5} />
                        Kündigung zurückziehen
                      </button>
                    ) : (
                      <button
                        data-testid="cancel-sub-btn"
                        onClick={cancelSub}
                        className="mv-btn-ghost"
                        style={{ color: "#b91c1c" }}
                      >
                        <Ban className="w-4 h-4" strokeWidth={1.5} />
                        Abo kündigen
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm" style={{ color: "var(--mv-text-muted)" }}>Kein Paket zugewiesen.</p>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="mv-card p-6">
          <p className="mv-label mb-4">Buchungs-Regeln</p>
          <div className="space-y-3">
            <div>
              <label className="block text-xs mb-1">Mindest-Vorlaufzeit (Stunden)</label>
              <input type="number" min="0" data-testid="min-lead-hours" className="mv-input" value={settings.min_lead_hours} onChange={(e) => setSettings({ ...settings, min_lead_hours: e.target.value })} />
              <p className="text-xs mt-1" style={{ color: "var(--mv-text-muted)" }}>Kunden müssen mindestens so viele Stunden im Voraus buchen.</p>
            </div>
            <div>
              <label className="block text-xs mb-1">Maximaler Vorlauf (Tage)</label>
              <input type="number" min="1" data-testid="max-lead-days" className="mv-input" value={settings.max_lead_days} onChange={(e) => setSettings({ ...settings, max_lead_days: e.target.value })} />
              <p className="text-xs mt-1" style={{ color: "var(--mv-text-muted)" }}>Maximal viele Tage in der Zukunft buchbar.</p>
            </div>
            <div>
              <label className="block text-xs mb-1">Slot-Raster (Min)</label>
              <input type="number" min="5" step="5" className="mv-input" value={settings.slot_step_minutes} onChange={(e) => setSettings({ ...settings, slot_step_minutes: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs mb-1">Termin-Erinnerung (Stunden vor Termin)</label>
              <input
                type="number"
                min="0"
                max="168"
                data-testid="reminder-hours-before"
                className="mv-input"
                value={settings.reminder_hours_before ?? 24}
                onChange={(e) => setSettings({ ...settings, reminder_hours_before: e.target.value })}
              />
              <p className="text-xs mt-1" style={{ color: "var(--mv-text-muted)" }}>
                Kunden können beim Buchen einen Erinnerungs-Toggle aktivieren. Diese E-Mail wird so viele Stunden vor dem Termin versendet. 0 deaktiviert das Feature komplett.
              </p>
            </div>
            <div>
              <label className="block text-xs mb-1">Zeitzone</label>
              <select
                data-testid="salon-timezone"
                className="mv-input"
                value={settings.timezone || "Europe/Berlin"}
                onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
              >
                <optgroup label="DACH">
                  <option value="Europe/Berlin">Europe/Berlin (Deutschland)</option>
                  <option value="Europe/Vienna">Europe/Vienna (Österreich)</option>
                  <option value="Europe/Zurich">Europe/Zurich (Schweiz)</option>
                </optgroup>
                <optgroup label="EU">
                  <option value="Europe/Amsterdam">Europe/Amsterdam</option>
                  <option value="Europe/Brussels">Europe/Brussels</option>
                  <option value="Europe/Copenhagen">Europe/Copenhagen</option>
                  <option value="Europe/Helsinki">Europe/Helsinki</option>
                  <option value="Europe/London">Europe/London</option>
                  <option value="Europe/Madrid">Europe/Madrid</option>
                  <option value="Europe/Oslo">Europe/Oslo</option>
                  <option value="Europe/Paris">Europe/Paris</option>
                  <option value="Europe/Prague">Europe/Prague</option>
                  <option value="Europe/Rome">Europe/Rome</option>
                  <option value="Europe/Stockholm">Europe/Stockholm</option>
                  <option value="Europe/Warsaw">Europe/Warsaw</option>
                </optgroup>
                <optgroup label="Andere">
                  <option value="UTC">UTC</option>
                  <option value="America/New_York">America/New_York</option>
                  <option value="America/Los_Angeles">America/Los_Angeles</option>
                </optgroup>
              </select>
              <p className="text-xs mt-1" style={{ color: "var(--mv-text-muted)" }}>Steuert „Heute", Slot-Berechnung und Buchungs-Validierung.</p>
            </div>
          </div>
        </div>

        <div className="mv-card p-6">
          <p className="mv-label mb-4">Mitarbeiter-Sichtbarkeit</p>
          <label className="flex items-start gap-3 text-sm">
            <input type="checkbox" className="mt-1" data-testid="staff-see-all" checked={!!settings.staff_see_all_bookings} onChange={(e) => setSettings({ ...settings, staff_see_all_bookings: e.target.checked })} />
            <span>
              <p className="font-medium mb-0.5">Mitarbeiter sehen alle Buchungen</p>
              <p className="text-xs" style={{ color: "var(--mv-text-muted)" }}>Wenn deaktiviert, sehen Mitarbeiter mit Rolle „Mitarbeiter" nur ihre eigenen Termine.</p>
            </span>
          </label>
          <label className="flex items-start gap-3 text-sm mt-4 pt-4 border-t" style={{ borderColor: "var(--mv-border)" }}>
            <input type="checkbox" className="mt-1" data-testid="staff-selection-required" checked={!!settings.staff_selection_required} onChange={(e) => setSettings({ ...settings, staff_selection_required: e.target.checked })} />
            <span>
              <p className="font-medium mb-0.5">Mitarbeiter-Auswahl beim Buchen erforderlich</p>
              <p className="text-xs" style={{ color: "var(--mv-text-muted)" }}>Wenn aktiviert, müssen Kunden beim Online-Buchen einen Mitarbeiter wählen. Die Option „Egal" wird ausgeblendet.</p>
            </span>
          </label>
        </div>
      </div>

      <div className="mt-8">
        <IcalSubscribe tokens={tokens} onRegenerated={load} />
      </div>

      <div className="mt-8">
        <p className="mv-label mb-4">Nutzer & Rollen</p>
        <div className="mv-card p-6 mb-4">
          <p className="text-sm font-medium mb-3">Neuen Salon-Admin oder Manager einladen</p>
          <form onSubmit={promote} className="grid md:grid-cols-5 gap-2">
            <input className="mv-input text-sm md:col-span-2" placeholder="E-Mail" type="email" required value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} />
            <input className="mv-input text-sm" placeholder="Name" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} />
            <input className="mv-input text-sm" placeholder="Passwort" type="password" required value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} />
            <select className="mv-input text-sm" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}>
              <option value="salon_admin">Salon-Admin</option>
              <option value="staff_manager">Manager</option>
              <option value="staff_member">Mitarbeiter</option>
            </select>
            <button data-testid="promote-user-btn" className="mv-btn md:col-span-5 justify-self-start"><Plus className="w-3 h-3" /> Einladen</button>
          </form>
          {err && <p className="text-sm text-red-500 mt-2">{err}</p>}
        </div>

        <div className="mv-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: "var(--mv-surface)" }}>
                <th className="text-left px-4 py-2.5 mv-label" style={{ color: "var(--mv-text-muted)" }}>E-Mail</th>
                <th className="text-left px-4 py-2.5 mv-label" style={{ color: "var(--mv-text-muted)" }}>Name</th>
                <th className="text-left px-4 py-2.5 mv-label" style={{ color: "var(--mv-text-muted)" }}>Rolle</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t" style={{ borderColor: "var(--mv-border)" }}>
                  <td className="px-4 py-2.5">{u.email}</td>
                  <td className="px-4 py-2.5">{u.name}</td>
                  <td className="px-4 py-2.5"><span className="text-xs px-2 py-1 rounded-full" style={{ background: "var(--mv-surface)" }}>{ROLE_LABEL[u.role] || u.role}</span></td>
                  <td className="px-2"><button data-testid={`delete-user-${u.id}`} onClick={() => removeUser(u.id)} className="p-2 opacity-60 hover:opacity-100"><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {planModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(44,38,33,0.5)" }}>
          <div className="bg-white rounded-2xl p-6 md:p-8 w-full max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="plan-switch-modal">
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="mv-label mb-1">Paket wechseln</p>
                <h2 className="font-display text-2xl">Wählen Sie Ihr neues Paket</h2>
                {currentPlan && (
                  <p className="text-xs mt-1" style={{ color: "var(--mv-text-muted)" }}>
                    Aktuell: <strong>{currentPlan.name}</strong>
                  </p>
                )}
              </div>
              <button onClick={() => { setPlanModalOpen(false); setConfirmPlanId(null); }} className="p-1.5 -m-1 rounded hover:bg-[var(--mv-surface)]">
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              {plans.map((p) => {
                const isCurrent = currentPlan && p.id === currentPlan.id;
                const isConfirm = confirmPlanId === p.id;
                return (
                  <div
                    key={p.id}
                    data-testid={`switch-plan-card-${p.slug}`}
                    className="mv-card p-5"
                    style={isCurrent ? { borderColor: "var(--mv-primary)", borderWidth: 2 } : {}}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-display text-xl">{p.name}</h3>
                      {isCurrent && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded font-medium" style={{ background: "var(--mv-primary)", color: "#fff" }}>
                          AKTUELL
                        </span>
                      )}
                    </div>
                    {p.on_sale ? (
                      <div className="mb-3">
                        <p>
                          <span className="font-display text-2xl" style={{ color: "var(--mv-primary)" }}>{p.sale_price.toFixed(2)}</span>
                          <span className="text-xs ml-1" style={{ color: "var(--mv-text-muted)" }}>€/Monat</span>
                          <span className="text-sm line-through ml-2" style={{ color: "var(--mv-text-muted)" }}>{p.monthly_price.toFixed(2)} €</span>
                        </p>
                        <p className="text-[10px] mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded" style={{ background: "var(--mv-primary)", color: "#fff" }}>
                          <Clock className="w-3 h-3" strokeWidth={1.8} /> {fmtCountdown(p.sale_until)}
                        </p>
                      </div>
                    ) : (
                      <p className="mb-3">
                        <span className="font-display text-2xl">{p.monthly_price.toFixed(2)}</span>
                        <span className="text-xs ml-1" style={{ color: "var(--mv-text-muted)" }}>€/Monat</span>
                      </p>
                    )}
                    {p.description && <p className="text-xs mb-3" style={{ color: "var(--mv-text-muted)" }}>{p.description}</p>}
                    <ul className="space-y-1 mb-4 text-xs">
                      {(p.features || []).slice(0, 8).map((f) => (
                        <li key={f} className="flex items-start gap-1.5">
                          <Check className="w-3 h-3 flex-shrink-0 mt-0.5" strokeWidth={2} style={{ color: "var(--mv-secondary)" }} />
                          <span>{FEATURE_LABELS_SHORT[f] || f}</span>
                        </li>
                      ))}
                      {(p.features || []).length > 8 && (
                        <li className="text-[10px] ml-4" style={{ color: "var(--mv-text-muted)" }}>
                          + {(p.features || []).length - 8} weitere
                        </li>
                      )}
                    </ul>

                    {isCurrent ? (
                      <button disabled className="mv-btn-ghost w-full justify-center opacity-60 cursor-not-allowed" data-testid={`switch-current-${p.slug}`}>
                        Aktuelles Paket
                      </button>
                    ) : isConfirm ? (
                      <div className="space-y-2">
                        <p className="text-xs leading-relaxed" style={{ color: "#b91c1c" }}>
                          {currentPlan && p.monthly_price < currentPlan.monthly_price
                            ? "Achtung: Bei einem Downgrade verlieren Sie ggf. Zugriff auf Funktionen Ihres aktuellen Pakets."
                            : "Bestätigen Sie den Wechsel zu diesem Paket."}
                        </p>
                        <div className="flex gap-1">
                          <button
                            onClick={() => setConfirmPlanId(null)}
                            disabled={planSwitchBusy}
                            className="mv-btn-ghost flex-1 justify-center text-xs"
                          >
                            Abbrechen
                          </button>
                          <button
                            data-testid={`confirm-switch-${p.slug}`}
                            onClick={() => switchPlan(p.id)}
                            disabled={planSwitchBusy}
                            className="mv-btn flex-1 justify-center text-xs"
                          >
                            {planSwitchBusy ? "..." : "Bestätigen"}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        data-testid={`switch-to-${p.slug}`}
                        onClick={() => setConfirmPlanId(p.id)}
                        className="mv-btn w-full justify-center"
                      >
                        {p.name} wählen <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            <p className="text-xs mt-4 text-center" style={{ color: "var(--mv-text-muted)" }}>
              Der Wechsel ist sofort wirksam. Beim Downgrade werden Funktionen ausgeblendet, Ihre Daten bleiben aber erhalten.
            </p>
          </div>
        </div>
      )}

      {/* Danger zone — only for salon-admin (super-admin uses /admin) */}
      {user?.role === "salon_admin" && (
        <div className="mt-12 p-6 rounded-xl border" style={{ borderColor: "#FCA5A5", background: "#FEF2F2" }} data-testid="danger-zone">
          <div className="flex items-start gap-3 mb-3">
            <AlertTriangle className="w-5 h-5 mt-0.5" strokeWidth={1.5} style={{ color: "#991B1B" }} />
            <div>
              <p className="mv-label" style={{ color: "#991B1B" }}>Danger zone</p>
              <h2 className="font-display text-xl" style={{ color: "#7F1D1D" }}>Daten-Reset</h2>
            </div>
          </div>
          <p className="text-sm mb-4" style={{ color: "#7F1D1D" }}>
            Alle Kunden und/oder Termine unwiderruflich löschen. Verwende dies nur, wenn du den Salon komplett neu starten möchtest.
          </p>
          <button
            onClick={() => setResetOpen(true)}
            data-testid="open-reset-dialog"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white"
            style={{ background: "#DC2626" }}
          >
            <Trash2 className="w-4 h-4" strokeWidth={1.6} /> Daten-Reset starten
          </button>
        </div>
      )}

      <DataResetDialog
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        salonName={salon?.name}
        onDone={() => setTimeout(() => setResetOpen(false), 3000)}
      />
    </div>
  );
}
